<template>
  <teleport to="head">
    <MetaTags :meta-data-fn="metaDataFn" />
    <ConfigState />
  </teleport>
  <div class="max-w-full max-h-[100vh] min-h-[100vh] flex flex-col overflow-y-hidden">
    <Header />
    <main id="scroll" class="flex-grow overflow-y-auto flex flex-col">
      <div class="flex-grow flex flex-col">
        <RouterView class="flex-grow" />
        <Footer />
      </div>
    </main>
    <ConfirmPopup />
    <SpinnerModal :show-spinner="showSpinner" :loading-message="spinnerMessage" :loading-message-params="spinnerMessageParams" />
    <OverlayPopup v-for="key in overlayPopups" :key="key" :overlay-popup-key="key" :overlay-popup-options="overlayPopupOptions" @closed="closeOverlayPopup(key)" />
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  getCurrentInstance,
  onServerPrefetch,
  watch
} from 'vue'
import { useRoute } from 'vue-router'
import { useUserStore } from '@/stores/UserStore.js'
import { useI18n } from 'vue-i18n'
import { ConfigState } from '@/components/ConfigState'
import { MetaTags } from '@/components/MetaTags'
import { useMeta } from '@/composables/Meta'
import Header from '@/components/Header.vue'
import Config from '@/utils/config'
import { useCacheStore } from '@/stores/CacheStore'
import ConfirmPopup from '@/components/ConfirmPopup.vue'
import { useSpinnerDisplay } from '@/composables/Spinner'
import { useOverlayPopup } from '@/composables/OverlayPopup'
import { useToastDisplay } from '@/composables/Toast'
import { useAppEventHandlers } from '@/composables/AppEventHandlers'
import Footer from '@/components/Footer.vue'

const { metaDataFn, updateMetaData } = useMeta()
const { t } = useI18n()
const app = getCurrentInstance()
useAppEventHandlers()
const cacheStore = useCacheStore()
const fullUrl = app?.appContext.config.globalProperties.fullUrl
const userStore = useUserStore()
const route = useRoute()
const { SpinnerModal, showSpinner, spinnerMessage, spinnerMessageParams } = useSpinnerDisplay()
const { OverlayPopup, closeOverlayPopup, overlayPopups, overlayPopupOptions } = useOverlayPopup()
useToastDisplay()

const resetMetaData = () => {
  return updateMetaData({
    language: computed(() => userStore.language),
    title: computed(() => t('common.meta_title')),
    description: computed(() => t('common.Meta_description')),
    image: computed(() => t('common.seo.image')),
    noIndex: computed(() => false),
    url: computed(() => (import.meta.env.SSR ? fullUrl : window.location.href)),
    next: computed(() => undefined),
    prev: computed(() => undefined),
  })
}

resetMetaData()

const country = computed((): string => {
  return (route.params.locale as string)?.split('-')[1] ?? userStore.country ?? Config.availableCountries[0]
})

const language = computed((): string => {
  return (route.params.locale as string)?.split('-')[0] ?? userStore.language ?? Config.languages[0].lang
})

onServerPrefetch(() => {
  if (!language.value) return
  userStore.language = language.value
})

watch(
  () => language.value,
  (newLanguage) => {
    if (!newLanguage) return
    userStore.language = newLanguage
  },
  { immediate: true }
)

watch(
  () => country.value,
  (newCountry) => {
    if (!newCountry) return
    userStore.country = newCountry
  },
  { immediate: true }
)

watch(
  () => userStore.language,
  (newLanguage) => {
    if (!newLanguage) return
    cacheStore.languageChanged(newLanguage)
  },
  { immediate: true }
)
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.15s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>